// @flow

import _ from 'lodash';

import ArrayUtils from '../utils/ArrayUtils';
import DateUtils from '../utils/DateUtils';

import ApiObject from './ApiObject';
import ApiConstants from '../constants/ApiConstants';

import OpeningHour from './OpeningHour';

export default class OpeningHours extends ApiObject {

  monday: Array<OpeningHour> = [];
  tuesday: Array<OpeningHour> = [];
  wednesday: Array<OpeningHour> = [];
  thursday: Array<OpeningHour> = [];
  friday: Array<OpeningHour> = [];
  saturday: Array<OpeningHour> = [];
  sunday: Array<OpeningHour> = [];

  // -- tools
  getForDay(day: number) {
    switch (day) {
      case ApiConstants.Day.MONDAY:
        return this.monday;
      case ApiConstants.Day.TUESDAY:
        return this.tuesday;
      case ApiConstants.Day.WEDNESDAY:
        return this.wednesday;
      case ApiConstants.Day.THURSDAY:
        return this.thursday;
      case ApiConstants.Day.FRIDAY:
        return this.friday;
      case ApiConstants.Day.SATURDAY:
        return this.saturday;
      case ApiConstants.Day.SUNDAY:
        return this.sunday;
      default:
        return [];
    }
  }

  addForDay(day: number, openingHour: OpeningHour) {
    switch (day) {
      case ApiConstants.Day.MONDAY:
        this.monday.push(openingHour);
        break;
      case ApiConstants.Day.TUESDAY:
        this.tuesday.push(openingHour);
        break;
      case ApiConstants.Day.WEDNESDAY:
        this.wednesday.push(openingHour);
        break;
      case ApiConstants.Day.THURSDAY:
        this.thursday.push(openingHour);
        break;
      case ApiConstants.Day.FRIDAY:
        this.friday.push(openingHour);
        break;
      case ApiConstants.Day.SATURDAY:
        this.saturday.push(openingHour);
        break;
      case ApiConstants.Day.SUNDAY:
        this.sunday.push(openingHour);
        break;
      default:
    }
  }

  removeForDay(day: number, openingHour: OpeningHour) {
    switch (day) {
      case ApiConstants.Day.MONDAY:
        this.monday = this.removeOpeningHourOnArray(this.monday, openingHour);
        break;
      case ApiConstants.Day.TUESDAY:
        this.tuesday = this.removeOpeningHourOnArray(this.tuesday, openingHour);
        break;
      case ApiConstants.Day.WEDNESDAY:
        this.wednesday = this.removeOpeningHourOnArray(this.wednesday, openingHour);
        break;
      case ApiConstants.Day.THURSDAY:
        this.thursday = this.removeOpeningHourOnArray(this.thursday, openingHour);
        break;
      case ApiConstants.Day.FRIDAY:
        this.friday = this.removeOpeningHourOnArray(this.friday, openingHour);
        break;
      case ApiConstants.Day.SATURDAY:
        this.saturday = this.removeOpeningHourOnArray(this.saturday, openingHour);
        break;
      case ApiConstants.Day.SUNDAY:
        this.sunday = this.removeOpeningHourOnArray(this.sunday, openingHour);
        break;
      default:
    }
  }

  removeOpeningHourOnArray(list: Array<OpeningHour>, toRemove: OpeningHour) {
    const res = [];

    _.each(list, (openingHour: OpeningHour) => {
      if (openingHour.opening !== toRemove.opening && openingHour.closing !== toRemove.closing) {
        res.push(openingHour);
      }
    });

    return res;
  }

  getSuggestOpeningHourForDay(day: number): OpeningHour {
    const allOpeningsHours:Array<OpeningHour> = this.getAllOpeningsHours();
    const openingHoursForDay = this.getForDay(day);

    let result = {};
    _.each(allOpeningsHours, (openingHour: OpeningHour) => {
      const key = `${openingHour.opening}_${openingHour.closing}`;
      if (_.isUndefined(result[key])) {
        result[key] = {
          openingHour,
          count: 1
        }
      } else {
        result[key].count++;
      }
    });

    // lower count for already used opening hours for this day.
    if (!_.isEmpty(openingHoursForDay)) {
      _.each(openingHoursForDay, (openingHour: OpeningHour) => {
        const key = `${openingHour.opening}_${openingHour.closing}`;
        result[key].count = -1;
      });
    }

    // find most present
    if (!_.isEmpty(result)) {
      let maxCount = 0;
      let max = !_.isEmpty(openingHoursForDay) ? openingHoursForDay[0] : allOpeningsHours[0];

      _.forIn(result, (value) => {
        if (value.count > maxCount) {
          maxCount = value.count;
          max = value.openingHour;
        }
      });

      // return a copy, not a reference.
      return max.copy();
    }

    return new OpeningHour();
  }

  getSuggestOpeningHour(): OpeningHour {
    const allOpeningsHours: Array<OpeningHour> = this.getAllOpeningsHours();

    let result = {};
    _.each(allOpeningsHours, (openingHour: OpeningHour) => {
      const key = `${openingHour.opening}_${openingHour.closing}`;
      if (_.isUndefined(result[key])) {
        result[key] = {
          openingHour,
          count: 1
        }
      } else {
        result[key].count++;
      }
    });

    // find most present
    if (!_.isEmpty(result)) {
      let maxCount = 0;
      let max = new OpeningHour;

      _.forIn(result, (value) => {
        if (value.count > maxCount) {
          maxCount = value.count;
          max = value.openingHour;
        }
      });

      // return a copy, not a reference
      return max.copy();
    }

    return new OpeningHour();
  }

  getAllOpeningsHours(): Array<OpeningHour> {
    return _.concat(this.monday, this.tuesday,
      this.wednesday, this.thursday, this.friday,
      this.saturday, this.sunday
    );
  }


  /**
   * Warning: moment.js week start at sunday, not monday
   */
  isDayOpen(dayIndex: number) {
    const openingHours: Array<OpeningHour> = this.getForDay(dayIndex);

    if (!_.isEmpty(openingHours)) {
      return true;
    }

    return false;
  }

  isOpenForDate(date) {
    const openingHours: Array<OpeningHour> = this.getForDay(DateUtils.getPmtDayIndex(date));

    return ArrayUtils.exists(openingHours, (openingHour: OpeningHour) => {
      return DateUtils.getTimestampForHourWithDate(date) >= openingHour.opening
        && DateUtils.getTimestampForHourWithDate(date) <= openingHour.closing
    });
  }

  /**
   * Warning: moment.js week start at sunday, not monday
   */
  generateHoursForDay(dayIndex: number, minuteInterval: number = 15): Array<number> {
    const openingHours: Array<OpeningHour> = this.getForDay(dayIndex);
    let hours = [];

    _.each(openingHours, (openingHour: OpeningHour) => {
      hours = _.concat(hours, openingHour.getInterval(minuteInterval));
    });

    // delete doublons
    let finalHours = _.uniq(hours);

    // sort by date
    finalHours = _.sortBy(finalHours, (slot) => {
      return slot;
    });

    return finalHours;
  }

  // -- serialization

  postJsonAssignation(data: Object) {
    super.postJsonAssignation(data);

    this.monday = [];
    _.each(data.monday, (openingHourData: Object) => {
      const openingHour = new OpeningHour();
      openingHour.fromJson(openingHourData);
      this.monday.push(openingHour);
    });

    this.tuesday = [];
    _.each(data.tuesday, (openingHourData: Object) => {
      const openingHour = new OpeningHour();
      openingHour.fromJson(openingHourData);
      this.tuesday.push(openingHour);
    });

    this.wednesday = [];
    _.each(data.wednesday, (openingHourData: Object) => {
      const openingHour = new OpeningHour();
      openingHour.fromJson(openingHourData);
      this.wednesday.push(openingHour);
    });

    this.thursday = [];
    _.each(data.thursday, (openingHourData: Object) => {
      const openingHour = new OpeningHour();
      openingHour.fromJson(openingHourData);
      this.thursday.push(openingHour);
    });

    this.friday = [];
    _.each(data.friday, (openingHourData: Object) => {
      const openingHour = new OpeningHour();
      openingHour.fromJson(openingHourData);
      this.friday.push(openingHour);
    });

    this.saturday = [];
    _.each(data.saturday, (openingHourData: Object) => {
      const openingHour = new OpeningHour();
      openingHour.fromJson(openingHourData);
      this.saturday.push(openingHour);
    });

    this.sunday = [];
    _.each(data.sunday, (openingHourData: Object) => {
      const openingHour = new OpeningHour();
      openingHour.fromJson(openingHourData);
      this.sunday.push(openingHour);
    });
  }
}



// WEBPACK FOOTER //
// ./sdk/scripts/entities/OpeningHours.js